// Minimal inline card-brand marks rendered next to the Stripe inputs so users
// know which networks are accepted before they start typing. SVG (not
// <img src=…>) for two reasons:
//   1. No extra network request, no broken-image risk in the host page.
//   2. CSS-immune like the rest of our inline-styled elements — host themes
//      can't strip the fill/size.
//
// Each logo is sized inside a 40×26 box matching a standard credit-card aspect.

type LogoProps = { title: string; children: React.ReactNode };

function Frame({ title, children }: LogoProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 40 26"
      role="img"
      aria-label={title}
      style={{ width: 40, height: 26, display: 'block', flexShrink: 0 }}
    >
      <title>{title}</title>
      {children}
    </svg>
  );
}

// Approximation of the Visa wordmark — dark blue VISA text on a white card
// with a light border. Recognizable at this size without needing the full
// brand artwork.
function VisaLogo() {
  return (
    <Frame title="Visa">
      <rect x="0.5" y="0.5" width="39" height="25" rx="3" fill="#ffffff" stroke="#d2d2d9" />
      <text
        x="20"
        y="18"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="900"
        fontSize="12"
        fill="#1a1f71"
        fontStyle="italic"
        letterSpacing="0.5"
      >
        VISA
      </text>
    </Frame>
  );
}

// Two overlapping circles — Mastercard's signature mark.
function MastercardLogo() {
  return (
    <Frame title="Mastercard">
      <rect x="0.5" y="0.5" width="39" height="25" rx="3" fill="#ffffff" stroke="#d2d2d9" />
      <circle cx="16" cy="13" r="7" fill="#eb001b" />
      <circle cx="24" cy="13" r="7" fill="#f79e1b" />
      <path d="M20 7.7a7 7 0 0 1 0 10.6 7 7 0 0 1 0-10.6z" fill="#ff5f00" />
    </Frame>
  );
}

// American Express — blue card with white "AMEX" wordmark.
function AmexLogo() {
  return (
    <Frame title="American Express">
      <rect x="0.5" y="0.5" width="39" height="25" rx="3" fill="#2e77bb" stroke="#2e77bb" />
      <text
        x="20"
        y="17"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="900"
        fontSize="9"
        fill="#ffffff"
        letterSpacing="0.5"
      >
        AMEX
      </text>
    </Frame>
  );
}

export function CardBrandLogos() {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
      }}
      aria-label="Akzeptierte Kreditkarten"
    >
      <VisaLogo />
      <MastercardLogo />
      <AmexLogo />
    </div>
  );
}
